import React, { useState, useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import { getScreams } from "../redux/actions/dataActions";
import { fetchUserProfile, updateUserProfile } from "../util/api/user.js";
import { useActionState } from 'react';
import { ChessSocialContext } from "../util/ChessSocialContext";
import ProfilePictureUpload from '../components/profile/ProfilePictureUpload.js';

const EditProfile = (props) => {
    const { isSidebarCollapsed } = useContext(ChessSocialContext);

    // Profile data
    const [profile, setProfile] = useState({
        status: props?.user?.credentials?.status || "",
        bio: props?.user?.credentials?.biography || "",
        youtube: props?.user?.credentials?.youtubeLink || "",
        tiktok: props?.user?.credentials?.tiktokLink || "",
        instagram: props?.user?.credentials?.instagramLink || "",
        twitter: props?.user?.credentials?.twitterLink || ""
    });

    // Photo URLs
    const [photoUrl, setPhotoUrl] = useState(props?.user?.credentials?.photoUrl || "");
    const [pendingPhotoUrl, setPendingPhotoUrl] = useState(props?.user?.credentials?.pendingPhotoUrl || "");

    // Add state to track original values for comparison
    const [originalValues, setOriginalValues] = useState({
        status: "",
        bio: "",
        youtube: "",
        tiktok: "",
        instagram: "",
        twitter: "",
        photoUrl: "",
        pendingPhotoUrl: ""
    });

    // Add state for unsaved changes tracking and confirmation modal
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    const [characters, setCharacters] = useState(profile.status?.length || 0); // Number of characters in status field
    const [result, setResult] = useState({});

    // Social media links
    const [links, setLinks] = useState({
        youtube: { name: 'www.youtube.com/username', icon: 'fa-youtube', value: profile.youtube },
        tiktok: { name: 'www.tiktok.com/username', icon: 'fa-tiktok', value: profile.tiktok },
        instagram: { name: 'www.instagram.com/username', icon: 'fa-instagram', value: profile.instagram },
        twitter: { name: 'www.twitter.com/username', icon: 'fa-x-twitter', value: profile.twitter }
    });

    // Error messages for each link field
    const [error, setError] = useState({
        youtube: "",
        tiktok: "",
        instagram: "",
        twitter: ""
    });

    // Add state for success popup
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);

    // Add effect to communicate unsaved changes to parent component
    useEffect(() => {
        

        // If parent component provided an onUnsavedChanges handler, call it
        if (props.onUnsavedChanges && typeof props.onUnsavedChanges === 'function') {
            
            props.onUnsavedChanges(hasUnsavedChanges);
        }

        // Also store this state in a global variable that can be checked by the parent
        window.editProfileHasUnsavedChanges = hasUnsavedChanges;
        
    }, [hasUnsavedChanges, props.onUnsavedChanges]);

    // Capture original values when credentials load or change
    useEffect(() => {
        if (props?.user?.credentials) {
            setOriginalValues({
                status: props.user.credentials.status || "",
                bio: props.user.credentials.biography || "",
                youtube: props.user.credentials.youtubeLink || "",
                tiktok: props.user.credentials.tiktokLink || "",
                instagram: props.user.credentials.instagramLink || "",
                twitter: props.user.credentials.twitterLink || "",
                photoUrl: props.user.credentials.photoUrl || "",
                pendingPhotoUrl: props.user.credentials.pendingPhotoUrl || ""
            });
        }
    }, [props.user.credentials]);

    // Check for unsaved changes whenever form values change
    useEffect(() => {
        const hasChanges =
            profile.status !== originalValues.status ||
            profile.bio !== originalValues.bio ||
            links.youtube.value !== originalValues.youtube ||
            links.tiktok.value !== originalValues.tiktok ||
            links.instagram.value !== originalValues.instagram ||
            links.twitter.value !== originalValues.twitter ||
            photoUrl !== originalValues.photoUrl ||
            pendingPhotoUrl !== originalValues.pendingPhotoUrl;

        setHasUnsavedChanges(hasChanges);
    }, [profile, links, originalValues, photoUrl, pendingPhotoUrl]);

    // Add custom tab navigation interception
    useEffect(() => {
        // Function to intercept navigation attempts
        const handleTabNavigation = (event) => {
            // Only intercept if we have unsaved changes
            if (hasUnsavedChanges) {
                const target = event.target;

                // Check if the clicked element is a navigation link or tab button
                // Looking for elements with href or tab-related classes/data attributes
                if (
                    target.tagName === 'A' ||
                    target.closest('a') ||
                    target.dataset.tab ||
                    target.closest('[data-tab]') ||
                    target.classList.contains('nav-link') ||
                    target.classList.contains('tab-button') ||
                    target.closest('.nav-link') ||
                    target.closest('.tab-button')
                ) {
                    // Prevent the default navigation
                    event.preventDefault();
                    event.stopPropagation();

                    // Get the destination URL or tab
                    let destination;
                    if (target.href) {
                        destination = target.href;
                    } else if (target.closest('a')?.href) {
                        destination = target.closest('a').href;
                    } else if (target.dataset.tab) {
                        destination = target.dataset.tab;
                    } else if (target.closest('[data-tab]')?.dataset.tab) {
                        destination = target.closest('[data-tab]').dataset.tab;
                    }

                    // Show confirmation modal with the pending action
                    if (destination) {
                        setPendingAction(() => () => {
                            window.location.href = destination;
                        });
                        setShowConfirmModal(true);
                    }
                }
            }
        };

        // Add click event listener to the entire document
        document.addEventListener('click', handleTabNavigation, true);

        // Clean up the event listener
        return () => {
            document.removeEventListener('click', handleTabNavigation, true);
        };
    }, [hasUnsavedChanges]);

    // Add beforeunload event listener to catch page navigation/refresh
    // This will still show browser dialog for refresh/close/external navigation
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                // Standard way to show browser confirmation dialog
                e.preventDefault();
                e.returnValue = ''; // Required for Chrome
                return ''; // Required for other browsers
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    // Update profile data as they are entered
    const handleChange = (input) => {
        setProfile({ ...profile, [input.target.name]: input.target.value });

        // Set the total length of status
        if (input.target.name == "status") {
            setCharacters(input.target.value.length);
        }
    };

    // Update links as they are entered
    const handleLinkChange = (input) => {
        const socialKey = input.target.name;
        const newValue = input.target.value;

        

        if (!links[socialKey]) {
            console.error("EditProfile: Invalid social key:", socialKey);
            return;
        }

        setLinks(prevLinks => ({
            ...prevLinks,
            [socialKey]: { ...prevLinks[socialKey], value: newValue }
        }));
    };

    // Validate social media links, return full link if valid, return an error message if invalid
    const validateLink = (link, platformName) => {
        // Extract just the domain name from the platformName (e.g. "youtube" from "www.youtube.com/username")
        const domainRegex = /www\.([^.]+)\.com\/.*/;
        const match = platformName.match(domainRegex);
        const domain = match ? match[1] : null;

        
        

        if (!domain) {
            return "Invalid platform format";
        }

        // Create the proper domain pattern
        const start = "https://";
        const domainSite = "www." + domain + ".com/"; // Valid link must have www.domain.com/

        

        // Check if the URL already has https:// or http:// prefix - if so, trim it for processing
        let processedLink = link;
        if (link.toLowerCase().startsWith('https://')) {
            processedLink = link.substring(8); // Remove https://
        } else if (link.toLowerCase().startsWith('http://')) {
            processedLink = link.substring(7); // Remove http://
        }

        const lower = processedLink.toLowerCase();
        const index = lower.indexOf(domainSite); // Compare without case sensitivity

        if (index == -1) {
            return "Link must include " + domainSite;
        }

        let url = processedLink.substring(index + domainSite.length); // Rest of link

        // Invalid if domain is encountered again
        if (url.toLowerCase().includes(domainSite)) {
            return "Invalid link";
        }

        // Remove extra '/'
        let i = 0;
        while (url.charAt(i) === "/") {
            url = url.substring(1);
            i++;
        }

        // Return the full URL with https:// prefix
        return start.concat(domainSite, url);
    }

    // Handle page navigation with confirmation if needed
    const handleNavigate = (destination) => {
        if (hasUnsavedChanges) {
            setPendingAction(() => () => {
                window.location.href = destination;
            });
            setShowConfirmModal(true);
        } else {
            window.location.href = destination;
        }
    };

    // Save changes to profile and update data in Firebase
    const handleSubmit = () => {
        let isValid = true;
        const newData = {};

        // Clear all previous errors first
        setError({
            youtube: "",
            tiktok: "",
            instagram: "",
            twitter: ""
        });

        // Validate and process social media links
        Object.keys(links).forEach((social) => {
            // Link is modified
            if (links[social].value !== originalValues[social]) {
                // If link is blank, accept it without validation
                if (!links[social].value) {
                    setError(prev => ({ ...prev, [social]: "" }));
                    setLinks(prev => ({ ...prev, [social]: { ...prev[social], value: "" } }));
                    // Add blank value directly to newData
                    switch (social) {
                        case 'youtube':
                            newData.youtubeLink = "";
                            break;
                        case 'tiktok':
                            newData.tiktokLink = "";
                            break;
                        case 'instagram':
                            newData.instagramLink = "";
                            break;
                        case 'twitter':
                            newData.twitterLink = "";
                            break;
                    }
                    return;
                }

                // Otherwise validate the link
                const validate = validateLink(links[social].value, links[social].name);
                

                // Link is invalid
                if (!(validate.startsWith("https://"))) {
                    setError(prev => ({ ...prev, [social]: validate }));
                    isValid = false;
                    return;
                }

                setError(prev => ({ ...prev, [social]: "" })); // Clear error message
                setLinks(prev => ({ ...prev, [social]: { ...prev[social], value: validate } })); // Update link
                // Add valid link to newData as pending
                switch (social) {
                    case 'youtube':
                        newData.pendingYoutubeLink = [validate, false];
                        break;
                    case 'tiktok':
                        newData.pendingTikTokLink = [validate, false];
                        break;
                    case 'instagram':
                        newData.pendingInstagramLink = [validate, false];
                        break;
                    case 'twitter':
                        newData.pendingTwitterLink = [validate, false];
                        break;
                }
            }
        });

        // Stop if links are invalid
        if (!isValid) {
            
            
            // Set a global validation error flag that can be checked by parent components
            window.editProfileHasValidationErrors = true;
            
            // Make sure errors are visible by scrolling to the first error
            setTimeout(() => {
                const firstErrorElement = document.querySelector('.edit-profile-container [style*="color: #ff0000"]');
                if (firstErrorElement) {
                    firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                
                // Auto-clear the error flag after a while
                setTimeout(() => {
                    window.editProfileHasValidationErrors = false;
                }, 5000);
            }, 100);
            
            return;
        }

        // Check if status has changed
        if (profile.status !== originalValues.status) {
            if (!profile.status) {
                // Allow empty status
                newData.status = "";
            } else {
                newData.pendingStatus = [profile.status, false];
            }
        }

        // Check if bio has changed
        if (profile.bio !== originalValues.bio) {
            if (!profile.bio) {
                // Allow empty bio
                newData.biography = "";
            } else {
                newData.pendingBiography = [profile.bio, false];
            }
        }

        // Only proceed if we have changes to submit
        if (Object.keys(newData).length > 0) {
            
            updateUser(newData);
            setHasUnsavedChanges(false);
            
            // Clear validation error flag
            window.editProfileHasValidationErrors = false;
            
            // Show success popup
            setShowSuccessPopup(true);
            // Hide popup after 3 seconds
            setTimeout(() => {
                setShowSuccessPopup(false);
            }, 3000);

            // Scroll to the top of the EditProfile component
            const editProfileContainer = document.querySelector('.edit-profile-container');
            if (editProfileContainer) {
                editProfileContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }

        // If this was called from the confirmation modal, close it
        if (showConfirmModal) {
            setShowConfirmModal(false);
            // Only proceed with the pending action if validation passed
            if (isValid && pendingAction) {
                setTimeout(pendingAction, 100);
                setPendingAction(null);
            }
        }
    };

    // Handle "Don't Save" option from confirmation modal
    const handleDiscardChanges = () => {
        setShowConfirmModal(false);
        setHasUnsavedChanges(false);

        if (pendingAction) {
            // Execute the pending action without saving
            setTimeout(pendingAction, 100);
            setPendingAction(null);
        }
    };

    // Need to be fixed
    const updateUser = async (data) => {
        try {
            const result = await updateUserProfile(data);
            setResult(result);
        } catch (error) {
            
        }
    };

    // Initial check of global unsaved changes flag
    useEffect(() => {
        
        

        // Make sure it starts as false when component mounts
        if (window.editProfileHasUnsavedChanges) {
            
            window.editProfileHasUnsavedChanges = false;
        }

        return () => {
            
        };
    }, []);

    // Handle profile picture upload completion
    const handleProfilePictureUpload = (newPendingPhotoUrl) => {
        setPendingPhotoUrl(newPendingPhotoUrl);
        setHasUnsavedChanges(true);
        
        // Set global flag for unsaved changes
        window.editProfileHasUnsavedChanges = true;
    };

    return (
        <div style={{ maxWidth: isSidebarCollapsed ? 'calc(100vw - 175px)' : 'calc(100vw - 300px)', width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
            {/* Success Popup */}
            {showSuccessPopup && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    padding: '20px 30px',
                    borderRadius: '10px',
                    boxShadow: '0 0 20px rgba(255, 215, 0, 0.3)',
                    border: '2px solid rgba(255, 215, 0, 0.5)',
                    color: 'white',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    animation: 'fadeIn 0.3s ease-out'
                }}>
                    <i className="fa-solid fa-check-circle" style={{
                        color: '#58b047',
                        fontSize: '24px'
                    }}></i>
                    <span style={{
                        fontSize: '16px',
                        fontWeight: 'bold'
                    }}>
                        Profile changes sent for review!
                    </span>
                </div>
            )}

            {/* Add animation styles */}
            <style>
                {`
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                            transform: translate(-50%, -20px);
                        }
                        to {
                            opacity: 1;
                            transform: translate(-50%, 0);
                        }
                    }
                `}
            </style>

            {/* Unsaved Changes Confirmation Modal */}
            {showConfirmModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: '#121220',
                        borderRadius: '10px',
                        padding: '30px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 0 30px rgba(255, 215, 0, 0.3)',
                        border: '2px solid rgba(255, 215, 0, 0.5)',
                    }}>
                        <h2 style={{
                            color: 'gold',
                            fontSize: '24px',
                            marginBottom: '20px',
                            textAlign: 'center'
                        }}>
                            Unsaved Changes
                        </h2>

                        <p style={{
                            color: 'white',
                            fontSize: '16px',
                            lineHeight: '1.5',
                            marginBottom: '25px',
                            textAlign: 'center'
                        }}>
                            You have unsaved changes to your profile. Would you like to save them before leaving?
                        </p>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '15px',
                            marginBottom: '15px' // Add margin to separate the button rows
                        }}>
                            <button
                                onClick={handleSubmit}
                                id="saveChangesBtn"
                                style={{
                                    padding: '12px 20px',
                                    backgroundColor: '#ff0050',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    fontWeight: 'bold',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    flex: 1,
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Save Changes
                            </button>

                            <button
                                onClick={handleDiscardChanges}
                                id="discardChangesBtn"
                                style={{
                                    padding: '12px 20px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    borderRadius: '5px',
                                    fontWeight: 'bold',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    flex: 1,
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Don't Save
                            </button>
                            <button
                            onClick={() => setShowConfirmModal(false)}
                            style={{
                                padding: '14px 20px',
                                backgroundColor: 'rgba(255, 215, 0, 0.15)',
                                color: '#ffd700',
                                border: '2px solid rgba(255, 215, 0, 0.6)',
                                borderRadius: '5px',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                cursor: 'pointer',
                                width: '100%',
                                transition: 'all 0.3s ease',
                                display: 'block', /* Ensure display is block */
                                marginTop: '0px', /* Add explicit top margin */
                                boxShadow: '0 2px 8px rgba(255, 215, 0, 0.2)' /* Add subtle glow */
                            }}
                        >
                            <i className="fa-solid fa-pencil" style={{ marginRight: '8px' }}></i>
                            Continue Editing
                        </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="edit-profile-container" style={{
                width: '100%',
                padding: '20px',
                backgroundColor: '#121220',
                borderRadius: '15px',
                boxShadow: '0 0 40px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 215, 0, 0.2)',
                backgroundImage: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.2) 100%)',
                overflow: 'hidden',
                marginBottom: '30px',
                scrollbarWidth: 'thin',
                position: 'relative'
            }}>

                {/* Add Save Button at bottom of container - similar to bookmark.js */}
                <div style={{
                    position: 'absolute',
                    bottom: '25px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1000,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '5px',
                }}>
                    <button
                        onClick={handleSubmit}
                        style={{
                            padding: '16px',
                            fontSize: "16px",
                            fontWeight: 'bold',
                            width: '100%',
                            color: "black",
                            backgroundColor: "#ff0050",
                            border: "none",
                            borderRadius: '8px',
                            cursor: "pointer",
                            transition: 'all 0.3s ease',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            boxShadow: '0 4px 12px rgba(255, 0, 80, 0.3)'
                        }} >
                        Save Changes
                    </button>
                </div>

                {/* Header Title */}
                <div style={{
                    textAlign: 'center',
                    marginBottom: '25px',
                    borderBottom: '2px solid rgba(255, 215, 0, 0.3)',
                    paddingBottom: '20px'
                }}>
                    <h1 style={{
                        color: 'gold',
                        fontSize: '28px',
                        textTransform: 'uppercase',
                        letterSpacing: '2px',
                        margin: '0',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px'
                    }}>
                        <i className="fa-solid fa-chess-knight"></i>
                        Edit Your Profile
                    </h1>
                    

                </div>

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    paddingBottom: '80px' // Add padding to bottom to ensure content doesn't get hidden behind the button
                }}>
                    {/* Profile Picture Section */}
                   

                    <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '20px'
                    }}>
                        {/* Profile Picture Section */}
                        <div className="profile-section" style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        borderRadius: '12px',
                        padding: '25px',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                        border: '1px solid rgba(255, 215, 0, 0.1)',
                        width: '38%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div className="section-header" style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '20px',
                            gap: '10px'
                        }}>
                            <h2 style={{
                                margin: '0',
                                color: 'gold',
                                fontSize: '24px',
                                fontWeight: 'bold'
                            }}>
                                                           <i className="fa-solid fa-camera" style={{ marginRight: '10px' }}></i>
                                                           Profile Picture
                            </h2>
                        </div>
                        <ProfilePictureUpload 
                            userId={props?.user?.credentials?.userId}
                            photoUrl={photoUrl}
                            pendingPhotoUrl={pendingPhotoUrl}
                            setPhotoUrl={setPhotoUrl}
                            setPendingPhotoUrl={setPendingPhotoUrl}
                            size="large"
                            onUploadComplete={handleProfilePictureUpload}
                        />

                        <div style={{
                            position: 'relative',
                            marginBottom: '5px',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                           
                        </div>

                    </div>

                    {/* Status Section */}
                    <div className="profile-section" style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        borderRadius: '12px',
                        padding: '25px',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                        border: '1px solid rgba(255, 215, 0, 0.1)',
                        width: '62%'
                    }}>
                        <div className="section-header" style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '20px',
                            gap: '10px'
                        }}>
                            <i className="fa-solid fa-quote-left" style={{ color: 'gold', fontSize: '18px' }}></i>
                            <h2 style={{
                                margin: '0',
                                color: 'gold',
                                fontSize: '24px',
                                fontWeight: 'bold'
                            }}>
                                Status
                            </h2>
                        </div>

                        <div style={{
                            position: 'relative',
                            marginBottom: '5px',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <textarea
                                id="status"
                                type="text"
                                name="status"
                                placeholder={'Shown beside @Username on your profile.'}
                                value={profile.status}
                                className='placeholding'
                                maxLength={15}
                                onChange={handleChange}
                                style={{
                                    width: "97%",
                                    padding: "14px 15px",
                                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                                    fontSize: "16px",
                                    color: 'white',
                                    border: "2px solid rgba(255, 215, 0, 0.5)",
                                    borderRadius: '8px',
                                    outline: 'none',
                                    transition: 'all 0.3s ease',
                                    height: '20px'
                                }}
                            /><div style={{
                                fontSize: "13px",
                                color: characters >= 13 ? "#ff9900" : "rgba(255, 255, 255, 0.5)",
                                textAlign: 'right',
                                fontWeight: characters >= 13 ? 'bold' : 'normal',
                                transition: 'all 0.3s ease',
                                position: 'absolute',
                                right: '40px',
                                top: '33%'
                            }}>
                                {15 - characters}/15 characters remaining...
                            </div>
                        </div>

                         {/* Biography Section */}
                
                        <div className="section-header" style={{
                            display: 'flex',
                            flexDirection: 'column',
                            marginBottom: '20px',
                            gap: '10px',
                            marginTop: '30px'

                        }}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '10px'
                            }}>
                            <i className="fa-solid fa-book-open" style={{ color: 'gold', fontSize: '18px' }}></i>
                            <h2 style={{
                                margin: '0',
                                color: 'gold',
                                fontSize: '24px',
                                fontWeight: 'bold',
                            }}>
                                Biography
                            </h2>
                            </div>
                            <textarea
                            id="bio"
                            name="bio"
                            placeholder="Tell the world about yourself and your chess journey."
                            value={profile.bio}
                            onChange={handleChange}
                            style={{
                                width: "95%",
                                height: '180px',
                                padding: "15px",
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                fontSize: "15px",
                                color: "white",
                                border: "2px solid rgba(255, 215, 0, 0.5)",
                                borderRadius: '8px',
                                resize: 'vertical',
                                minHeight: '150px',
                                maxHeight: '400px',
                                outline: 'none',
                                lineHeight: '1.5',
                                transition: 'all 0.3s ease'
                            }}
                        />
                        </div>

                    

                        
                    </div>

                    </div>

                    {/* Social Media Section */}
                    <div className="profile-section" style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        borderRadius: '12px',
                        padding: '25px',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                        border: '1px solid rgba(255, 215, 0, 0.1)'
                    }}>
                        <div className="section-header" style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '20px',
                            gap: '10px'
                        }}>
                            <i className="fa-solid fa-share-nodes" style={{ color: 'gold', fontSize: '18px' }}></i>
                            <h2 style={{
                                margin: '0',
                                color: 'gold',
                                fontSize: '24px',
                                fontWeight: 'bold'
                            }}>
                                Social Media
                            </h2>
                        </div>


                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '20px',
                        }}>
                            {Object.keys(links).map((social) => (
                                <div key={links[social].name} style={{
                                    position: 'relative',
                                    transition: 'all 0.3s ease',
                                    minHeight: '60px',
                                    marginBottom: '15px',

                                }}>
                                    <div style={{
                                        position: 'absolute',
                                        left: '15px',
                                        top: '40%',
                                        transform: 'translateY(-50%)',
                                        color: error[social] ? '#ff0000' : 'rgba(255, 255, 255, 0.6)',
                                        zIndex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '10px',
                                        pointerEvents: 'none'
                                    }}>
                                        <i className={`fa-brands ${links[social].icon}`} style={{ fontSize: '20px' }}></i>
                                    </div>

                                    <input
                                        id={links[social].name}
                                        type="text"
                                        name={social}
                                        onChange={handleLinkChange}
                                        value={links[social].value}
                                        placeholder={`${links[social].name.charAt(0) + links[social].name.slice(1)}`}
                                        style={{
                                            width: '80%',
                                            padding: "14px 15px 14px 40px",
                                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                                            fontSize: "14px",
                                            color: "white",
                                            border: error[social] ? '2px solid #ff0000' : '2px solid rgba(255, 215, 0, 0.5)',
                                            borderRadius: '8px',
                                            outline: 'none',
                                            transition: 'all 0.3s ease'
                                        }}
                                    />

                                    {error[social] && (
                                        <div style={{
                                            position: 'absolute',
                                            left: '0',
                                            bottom: '-18px',
                                            color: '#ff0000',
                                            fontSize: '12px',
                                            width: '100%',
                                            textAlign: 'left',
                                            paddingLeft: '5px'
                                        }}>
                                            {error[social]}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                   

                </div>


            </div>


        </div>
    );
};

const mapStateToProps = (state) => ({
    data: state.data,
    user: state.user,
});

export default connect(mapStateToProps, { getScreams })(EditProfile);